import { Button, Flex, Input } from '@dnb/eufemia';
import { shield_medium as ShieldMediumIcon } from '@dnb/eufemia/icons';
import { useEufemiaForm } from '@portals/shared-frontend/hooks';
import { type JSX, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { verifyEmail } from '@/api/user';
import Container from '@/components/Container';
import LoadingModal from '@/components/LoadingModal';
import ModalPage from '@/components/ModalPage';

const verifyEmailFormSchema = z.object({
  code: z.string().min(6).max(6),
});

type VerifyEmailProps = {
  email: string;
};

export default function VerifyEmail({ email }: VerifyEmailProps): JSX.Element {
  const navigate = useNavigate();

  const {
    controller: { formError },
    register,
    handleSubmit,
    submitting,
  } = useEufemiaForm(verifyEmailFormSchema, {});

  const onSubmit = handleSubmit(
    useCallback(
      async ({ code }) => {
        await verifyEmail({ email: email, verificationCode: code });
        navigate('/sign-up/success');
      },
      [email, navigate],
    ),
  );

  return (
    <ModalPage
      description="You have now received an email with a verification code.
  Fill it in below to verify your account and complete sign up."
      subtitle="Sign up"
      title="You got mail!"
    >
      {submitting && <LoadingModal />}
      <Container size="small">
        <form onSubmit={onSubmit}>
          <Flex.Horizontal align="baseline">
            <Input
              autoFocus
              label="Confirmation code"
              placeholder="xxxxxx"
              size="large"
              stretch
              {...register.input('code')}
            />
          </Flex.Horizontal>

          <Flex.Horizontal align="baseline" top="large">
            <Button
              disabled={submitting}
              icon={ShieldMediumIcon}
              iconPosition="left"
              iconSize="medium"
              size="large"
              status={formError}
              type="submit"
            >
              Verify account
            </Button>
          </Flex.Horizontal>
        </form>
      </Container>
    </ModalPage>
  );
}
